import React, { useState } from 'react';
import './Trabaja.css';

const SEND_POSTULACION_URL = 'https://us-central1-souza-villa.cloudfunctions.net/sendPostulacionEmail';

interface PostulacionForm {
  nombre: string;
  email: string;
  telefono: string;
  area: string;
  cvLabel: string;
  cvBase64: string;
  cvMimeType: string;
}

interface FormState {
  sending: boolean;
  success: boolean;
  error: string;
}

const emptyForm: PostulacionForm = { nombre: '', email: '', telefono: '', area: '', cvLabel: '', cvBase64: '', cvMimeType: '' };
const emptyState: FormState = { sending: false, success: false, error: '' };

const Trabaja: React.FC = () => {
  const [formAbogado, setFormAbogado] = useState<PostulacionForm>({ ...emptyForm });
  const [formCaptador, setFormCaptador] = useState<PostulacionForm>({ ...emptyForm });
  const [stateAbogado, setStateAbogado] = useState<FormState>({ ...emptyState });
  const [stateCaptador, setStateCaptador] = useState<FormState>({ ...emptyState });
  const [formProcurador, setFormProcurador] = useState<PostulacionForm>({ ...emptyForm });
  const [stateProcurador, setStateProcurador] = useState<FormState>({ ...emptyState });

  const handleFile = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: (f: PostulacionForm) => void,
    form: PostulacionForm
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(',')[1];
      setter({ ...form, cvLabel: file.name, cvBase64: base64, cvMimeType: file.type });
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (
    tipo: string,
    form: PostulacionForm,
    setState: (s: FormState) => void,
    setForm: (f: PostulacionForm) => void
  ) => {
    setState({ sending: true, success: false, error: '' });
    try {
      const resp = await fetch(SEND_POSTULACION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo,
          nombre: form.nombre,
          email: form.email,
          telefono: form.telefono,
          area: form.area,
          cvBase64: form.cvBase64,
          cvFileName: form.cvLabel,
          cvMimeType: form.cvMimeType,
        }),
      });
      if (!resp.ok) throw new Error();
      setState({ sending: false, success: true, error: '' });
      setForm({ ...emptyForm });
    } catch {
      setState({ sending: false, success: false, error: 'Error al enviar. Intente nuevamente o escríbanos por WhatsApp.' });
    }
  };

  const renderCard = (
    num: string,
    titulo: string,
    subtitulo: string,
    desc: string,
    form: PostulacionForm,
    setForm: (f: PostulacionForm) => void,
    tipo: string,
    st: FormState,
    setSt: (s: FormState) => void,
    fields: { label: string; placeholder: string; key: keyof PostulacionForm; type?: string }[],
    requisitos?: string[]
  ) => (
    <div className="trabaja-card">
      <div className="trabaja-card-num">{num}</div>
      <div className="trabaja-card-title">{titulo}</div>
      <div className="trabaja-card-sub">{subtitulo}</div>
      <div className="trabaja-card-desc">{desc}</div>
      {requisitos && (
        <ul className="trabaja-card-reqs">
          {requisitos.map((r) => <li key={r}>{r}</li>)}
        </ul>
      )}

      <div className="trabaja-card-form">
        <div className="trabaja-form-title">Postular como {tipo}</div>

        {st.success ? (
          <div className="form-success visible">
            ✓ Postulación enviada. Nos comunicaremos a la brevedad.
          </div>
        ) : (
          <>
            {fields.map((f) => (
              <div className="form-group" key={f.key}>
                <label className="form-label">{f.label}</label>
                <input
                  className="form-control"
                  type={f.type || 'text'}
                  value={form[f.key] as string}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  placeholder={f.placeholder}
                />
              </div>
            ))}
            <div className="form-group">
              <label className="form-label">Currículum</label>
              <div className="upload-area">
                <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => handleFile(e, setForm, form)} />
                <div className="upload-area-text">
                  <span>Adjunte su CV</span> — PDF o Word (máx. 5MB)
                </div>
              </div>
              {form.cvLabel && <div style={{ fontSize: '0.7rem', color: 'var(--gold)', marginTop: '0.3rem' }}>{form.cvLabel}</div>}
            </div>
            {st.error && <p style={{ color: '#c0504a', fontSize: '0.78rem', marginTop: '0.5rem' }}>{st.error}</p>}
            <button
              className="btn-primary trabaja-submit-btn"
              disabled={st.sending}
              onClick={() => handleSubmit(tipo, form, setSt, setForm)}
            >
              {st.sending ? 'Enviando...' : 'Enviar Postulación →'}
            </button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <section id="trabaja" style={{ background: 'var(--dark-3)' }}>
      <div className="section-label">Únete al Equipo</div>
      <h2 className="section-title">Trabaja <em>con nosotros</em></h2>
      <div className="divider" />

      <div className="trabaja-layout">
        {renderCard(
          '01', 'Abogado/a', 'Profesional Jurídico',
          'Buscamos abogados con experiencia en derecho laboral, familia o migratorio. Valoramos el manejo de causas en tribunales de la Región del Biobío, habilidades de litigio y orientación al cliente. Ofrecemos un ambiente de trabajo colaborativo y oportunidades de desarrollo profesional.',
          formAbogado, setFormAbogado, 'abogado/a',
          stateAbogado, setStateAbogado,
          [
            { label: 'Nombre completo', placeholder: 'Su nombre completo', key: 'nombre' },
            { label: 'Correo electrónico', placeholder: 'correo@ejemplo.cl', key: 'email', type: 'email' },
            { label: 'Área de especialidad', placeholder: 'Ej: Derecho Laboral, Familia...', key: 'area' },
          ]
        )}
        {renderCard(
          '02', 'Captador/a', 'Ejecutivo Comercial',
          'Buscamos personas proactivas, con habilidades comunicacionales y orientación al cliente para captar usuarios que requieran asesoría jurídica. No se requiere formación en derecho. Ofrecemos sueldo base + atractivo sistema de bonos por desempeño. Ideal para quienes buscan ingresos variables con alto potencial.',
          formCaptador, setFormCaptador, 'captador/a',
          stateCaptador, setStateCaptador,
          [
            { label: 'Nombre completo', placeholder: 'Su nombre completo', key: 'nombre' },
            { label: 'Teléfono', placeholder: '+56 9 XXXX XXXX', key: 'telefono' },
            { label: 'Correo electrónico', placeholder: 'correo@ejemplo.cl', key: 'email', type: 'email' },
          ]
        )}
        {renderCard(
          '03', 'Procurador/a', 'Apoyo Jurídico',
          'Buscamos egresados/as de Derecho para apoyar la tramitación y gestión de causas del estudio. Una oportunidad para adquirir experiencia práctica junto a abogadas especializadas.',
          formProcurador, setFormProcurador, 'procurador/a',
          stateProcurador, setStateProcurador,
          [
            { label: 'Nombre completo', placeholder: 'Su nombre completo', key: 'nombre' },
            { label: 'Teléfono', placeholder: '+56 9 XXXX XXXX', key: 'telefono' },
            { label: 'Correo electrónico', placeholder: 'correo@ejemplo.cl', key: 'email', type: 'email' },
          ],
          [
            'Egresado/a de la carrera de Derecho',
            'Idealmente con grado aprobado (no excluyente)',
            'Disponibilidad de lunes a viernes en la mañana',
            'Posibilidad de teletrabajo un día a la semana',
          ]
        )}
      </div>
    </section>
  );
};

export default Trabaja;
